import { combineReducers, type Reducer } from 'redux';

import { detailsApi, violationsTabApi } from '../features';
import { aagGlobalApi } from '../shared/store/globalApis';
import { AAGReducer } from '../shared/store/reducer';

import getMinimalStore from './getMinimalStore';
import { MOCK_PROFILE_STATES, MOCK_USER_STATES } from './mockStates';

/**
 * This module sets up a mock Redux reducer and store for testing or running parcel server.
 *
 * - Combines mock reducers (`user`, `profile`) with actual application reducers (`AAGReducer`, `violationsTabApi`, `aagGlobalApi`).
 * - Provides a `getMockStore` utility to initialize a minimal Redux store conditionally based on the environment.
 *
 * Imports:
 * - `combineReducers`, `Reducer` from Redux
 * - Feature and global API slices
 * - `AAGReducer` from the shared reducer
 * - `getMinimalStore` utility to bootstrap a Redux store with middleware
 * - Mock state values for `user` and `profile`
 *
 * @module mockStoreWithReducer
 */

/**
 * A simple mock reducer for the `user` state.
 * @param {object} state - Initial user state.
 * @returns {object} The unchanged user state.
 */
const mockUserReducer = (state = MOCK_USER_STATES) => state;

/**
 * A simple mock reducer for the `profile` state.
 * @param {object} state - Initial profile state.
 * @returns {object} The unchanged profile state.
 */
const mockProfileReducer = (state = MOCK_PROFILE_STATES) => state;

/**
 * An empty reducer used in production-like environments where no mock state is needed.
 * @type {Reducer}
 */
const emptyReducer: Reducer = (state = {}): Record<string, unknown> => state;

/**
 * Combines all mock and real reducers into a single root reducer.
 * This includes mock `user` and `profile`, along with actual application reducers.
 * @type {Reducer}
 */
export const mockReducer = combineReducers({
  user: mockUserReducer,
  profile: mockProfileReducer,
  aag: AAGReducer,
  [violationsTabApi.reducerPath]: violationsTabApi.reducer,
  [detailsApi.reducerPath]: detailsApi.reducer,
  [aagGlobalApi.reducerPath]: aagGlobalApi.reducer,
});

/**
 * A preconfigured mock Redux store using the combined `mockReducer` and necessary middlewares.
 *
 * This store is primarily intended for use in tests or development environments where a
 * full production store setup is unnecessary. It includes mock state for `user` and `profile`,
 * along with API slices for `violationsTabApi` and `aagGlobalApi`.
 *
 * @constant
 * @type {Store}
 */
export const mockStore = getMinimalStore(mockReducer, {}, [
  violationsTabApi.middleware,
  aagGlobalApi.middleware,
]);

/**
 * Returns a Redux store configured with either the `mockReducer` or `emptyReducer`
 * based on the provided environment flag. Useful for unit testing or mocking the store.
 *
 * @param {boolean} isNotProduction - If true, uses `mockReducer`; otherwise, uses `emptyReducer`.
 * @returns {Store} A Redux store initialized with reducers and middleware.
 */
export const getMockStore = (isNotProduction: boolean) =>
  getMinimalStore(isNotProduction ? mockReducer : emptyReducer, {}, [
    violationsTabApi.middleware,
    aagGlobalApi.middleware,
  ]);
